import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { useIsAuthenticated } from '@azure/msal-react';
import ApiMServer from "../../services/ApiMServer";
import { FaMicrosoft, FaEnvelope, FaLock, FaUserPlus, FaKey, FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaEye, FaEyeSlash } from 'react-icons/fa';
import Reload from "../../components/reload/reload";
import { getApiErrorMessage, getTrialLoginBlockMessage } from "../../utilis/sguTrial";
import { clearLocalStoragePreservingUserPreferences } from "../../utilis/preservedLocalStorage";

import "./Login.css";

const Login = () => {
  const { instance } = useMsal();

  const isAuthenticated = useIsAuthenticated();

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setisLoggedIn] = useState(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState({ message: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const getAllowedUnitIds = (sessionData = {}) => {
    return [...new Set((sessionData?.allowedUnitIds || []).filter(Boolean))];
  };

  const hasAccessScope = (sessionData = {}) => {
    return Boolean(sessionData?.managerId) || getAllowedUnitIds(sessionData).length > 0;
  };

  const blockLoginIfTrialExpired = (sessionData) => {
    const trialMessage = getTrialLoginBlockMessage(sessionData);
    if (!trialMessage) return false;

    clearLocalStoragePreservingUserPreferences();
    sessionStorage.clear();

    setLoading(false);
    setError(trialMessage);
    return true;
  };

  const saveUnitsContext = (units = [], preferredUnitId = null) => {
    if (!Array.isArray(units) || !units.length) {
      return null;
    }

    const uniqueUnits = units.reduce((acc, unit) => {
      if (!unit?._id) {
        return acc;
      }

      const alreadyExists = acc.some((item) => item?._id === unit._id);
      if (!alreadyExists) {
        acc.push(unit);
      }
      return acc;
    }, []);

    if (!uniqueUnits.length) {
      return null;
    }

    const activeUnit =
      uniqueUnits.find((unit) => unit?._id === preferredUnitId) || uniqueUnits[0];

    localStorage.setItem('units', JSON.stringify(uniqueUnits));
    localStorage.setItem('cooperative', JSON.stringify(activeUnit));
    localStorage.setItem('activeUnitId', activeUnit?._id || '');

    const allEquipments = uniqueUnits.flatMap((unit) =>
      Array.isArray(unit?.equipments) ? unit.equipments : []
    );
    localStorage.setItem('equipment', JSON.stringify(allEquipments));

    const allOtherEquipments = uniqueUnits.flatMap((unit) =>
      Array.isArray(unit?.otherEquipments) ? unit.otherEquipments : []
    );
    localStorage.setItem('otherEquipments', JSON.stringify(allOtherEquipments));

    return activeUnit;
  };

  const handleUnitsByIds = async (unitIds = [], preferredUnitId = null, loginMicrosoft = false) => {
    const sanitizedUnitIds = [...new Set((unitIds || []).filter(Boolean))];

    if (!sanitizedUnitIds.length) {
      return false;
    }

    const { data } = await ApiMServer.post('/company/get/units', { arrayIds: sanitizedUnitIds });
    const units = Array.isArray(data) ? data : [];
    const activeUnit = saveUnitsContext(units, preferredUnitId || sanitizedUnitIds[0]);

    if (!activeUnit) {
      setLoading(false);
      setError('Nenhuma unidade associada ao usuário.');
      return false;
    }

    if (loginMicrosoft) {
      window.location.href = "/home";
    } else {
      setisLoggedIn(true);
    }

    return true;
  };

  const handleSessionAccess = async (sessionData, loginMicrosoft = false) => {
    const unitIds = getAllowedUnitIds(sessionData);
    const preferredUnitId = sessionData?.defaultUnitId || unitIds[0];

    if (sessionData?.managerId) {
      await handleCompany(sessionData.managerId, loginMicrosoft);
      return;
    }

    if (unitIds.length) {
      await handleUnitsByIds(unitIds, preferredUnitId, loginMicrosoft);
      return;
    }

    setLoading(false);
    setError('Nenhuma empresa ou unidade associada ao usuário.');
  };

  const handleEmailChange = (e) => {
    const inputEmail = e.target.value;
    setEmail(inputEmail);
    setError("");
  };

  const handleLogin = async () => {
    clearLocalStoragePreservingUserPreferences();

    if (!email || !senha) {
      setError('Preencha o campo E-mail e Senha!');
      return;
    }
    handleSignin(email, senha);
  };

  const handleSignin = async (email, password) => {
    setLoading(true);
    try {
      let { data } = await ApiMServer.post(`/websession`, { email, password, app: "SGU WEB" });

      if (blockLoginIfTrialExpired(data)) {
        return;
      }

      data.date = Date().toString();
      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('token', JSON.stringify(data.token));
      localStorage.setItem('theme', data?.theme === 'dark' ? 'dark' : 'light');

      if (!hasAccessScope(data)) {
        setLoading(false);
        setError('Nenhuma empresa ou unidade associada ao usuário.');
        return;
      }

      await handleSessionAccess(data);
    } catch (error) {
      setError(getApiErrorMessage(error?.response?.data, 'Falha ao carregar dados!'));
      setLoading(false);
    }
  };

  const handleCompany = async (id, loginMicrosoft = false) => {
    if (!id) {
      setError('ID da empresa é inválido.');
      return;
    }
    try {
      let { data } = await ApiMServer.post('/company/detail', { id });
      localStorage.setItem('cooperative', JSON.stringify(data));
      if (data?.otherEquipments?.length) {
        localStorage.setItem('otherEquipments', JSON.stringify(data.otherEquipments));
      }
      if (loginMicrosoft) {
        window.location.href = "/home";
      } else {
        setisLoggedIn(true);
      }
    } catch (error) {
      setError(getApiErrorMessage(error?.response?.data, 'Erro ao carregar os dados da empresa.'));
      clearLocalStoragePreservingUserPreferences();
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  const handleLoginMicrosoft = async (loginType) => {
    try {
      let response;
      if (loginType === "popup") {
        response = await instance.loginPopup(loginRequest);
      } else if (loginType === "redirect") {
        response = await instance.loginRedirect(loginRequest);
      }

      if (response && response.account) {
        const userEmail = response?.account?.username || "";
        const name = response?.account?.name || "";
        const { accessToken, account } = response;
        localStorage.setItem("msalToken", JSON.stringify(accessToken));
        localStorage.setItem("msalAccount", JSON.stringify(account));
        localStorage.setItem("emailMicrosoft", JSON.stringify(userEmail));
        await handleLoginMicrosoftEmail(userEmail, name);
      }
    } catch (error) {
      setError(getApiErrorMessage(error?.response?.data, 'Falha ao carregar os detalhes da empresa.'));
      setLoading(false);
    }
  };

  const handleLoginMicrosoftEmail = async (email, name) => {
    try {
      let { data } = await ApiMServer.post(`/websession-microsoft`, { email, name });

      if (blockLoginIfTrialExpired(data)) {
        return;
      }

      data.date = Date().toString();
      localStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('token', JSON.stringify(data.token));
      localStorage.setItem('theme', data?.theme === 'dark' ? 'dark' : 'light');

      if (!hasAccessScope(data)) {
        setLoading(false);
        setError('Nenhuma empresa ou unidade associada ao usuário.');
        return;
      }
      await handleSessionAccess(data, true);
    } catch (error) {
      console.log(error);
      setError("Erro ao carregar!");
      setLoading(false);
    }
  }

  const handleNavigation = useCallback(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};

    if (isAuthenticated && user?._id) {
      if (blockLoginIfTrialExpired(user)) {
        return;
      }
      if (!hasAccessScope(user)) {
        setError('Nenhuma empresa ou unidade associada ao usuário.');
        return;
      }
      navigate("/home");
    } else if (isLoggedIn) {
      navigate("/home");
    } else {
      navigate("/");
    }
  }, [navigate, isLoggedIn, isAuthenticated]);

  const goToSignUp = () => {
    navigate('/registrar');
  };

  const handleForgotPassword = async () => {
    if (!resetEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      setResetStatus({
        message: 'Por favor, informe um e-mail válido.',
        type: 'danger'
      });
      return;
    }

    setIsSubmitting(true);
    setResetStatus({ message: '', type: '' });

    try {
      await ApiMServer.post('/forgotpassword', { email: resetEmail });

      setResetStatus({
        message: 'E-mail de recuperação enviado com sucesso! Verifique sua caixa de entrada.',
        type: 'success'
      });

      setTimeout(() => {
        setForgotPasswordModal(false);
        setResetEmail('');
        setResetStatus({ message: '', type: '' });
      }, 5000);
    } catch (error) {
      let errorMessage = 'Erro ao processar sua solicitação. Tente novamente.';

      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }

      setResetStatus({
        message: errorMessage,
        type: 'danger'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetEmailChange = (e) => {
    setResetEmail(e.target.value);
    setResetStatus({ message: '', type: '' });
  };

  const openForgotPasswordModal = () => {
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResetEmail(email);
    }
    setForgotPasswordModal(true);
  };

  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "null");
      if (storedUser?._id) {
        blockLoginIfTrialExpired(storedUser);
      }
    } catch {
      // ignora JSON inválido no storage
    }
  }, []);

  useEffect(() => {
    handleNavigation();
  }, [handleNavigation]);

  return (
    <Container
      fluid
      className="d-flex justify-content-center align-items-center vh-100"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${'./images/capa.png'})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>

      {loading && <Reload />}

      <Container
        className="login-card p-4 border rounded-lg shadow"
        style={{
          maxWidth: '450px',
          backgroundColor: 'rgba(255, 255, 255, 0.65)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          boxShadow: '0 8px 32px 0 rgba(28, 66, 32, 0.37)',
          transition: 'all 0.3s ease'
        }}
      >
        <div className="text-center mb-4">
          <img
            src="./images/logo.png"
            alt="Logo"
            style={{ maxHeight: '60px', marginBottom: '15px' }}
            className="img-fluid"
          />
          <h2 className="fw-bold" style={{ color: '#1c4220' }}>SGU MOTOMCO</h2>
          <p className="text-muted">Sistema de Gerenciamento de Unidades</p>
        </div>

        <Form>
          <div className="mb-3">
            <Form.Label className="d-flex align-items-center mb-1 fw-bold" style={{ color: '#1c4220' }}>
              <FaEnvelope className="me-2" /> E-mail
            </Form.Label>
            <Form.Control
              className={`${error ? 'border-danger' : ''} rounded-pill`}
              type="email"
              placeholder="Digite seu e-mail"
              value={email}
              onChange={handleEmailChange}
              style={{
                padding: '12px 20px',
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(143, 173, 21, 0.3)',
                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            />
          </div>

          <div className="mb-2">
            <Form.Label className="d-flex align-items-center mb-1 fw-bold" style={{ color: '#1c4220' }}>
              <FaLock className="me-2" /> Senha
            </Form.Label>
            <div className="position-relative">
              <Form.Control
                className={`${error ? 'border-danger' : ''} rounded-pill`}
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha"
                value={senha}
                onKeyPress={handleKeyPress}
                onChange={(e) => {
                  setSenha(e.target.value);
                  setError('');
                }}
                style={{
                  padding: '12px 48px 12px 20px',
                  background: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(5px)',
                  border: '1px solid rgba(143, 173, 21, 0.3)',
                  boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Button
                type="button"
                variant="link"
                className="position-absolute top-50 translate-middle-y p-0 border-0"
                style={{
                  right: '16px',
                  color: '#1c4220',
                  lineHeight: 1
                }}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </Button>
            </div>
          </div>

          <div className="text-end mb-3">
            <Button
              variant="link"
              className="p-0"
              style={{ color: '#1c4220', textDecoration: 'none', fontSize: '0.9rem' }}
              onClick={openForgotPasswordModal}
            >
              <FaKey className="me-1" size={12} />
              Esqueci minha senha
            </Button>
          </div>

          {error &&
            <Alert
              className="p-2 mb-3 rounded-pill text-center"
              variant="danger"
              style={{
                fontSize: '0.9rem',
                background: 'rgba(220, 53, 69, 0.7)',
                backdropFilter: 'blur(5px)',
                border: '1px solid rgba(220, 53, 69, 0.2)',
                color: 'white'
              }}
            >
              {error}
            </Alert>
          }

          <Button
            variant="primary"
            className="w-100 py-2 mb-3 rounded-pill fw-bold"
            onClick={handleLogin}
            style={{
              background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.9), rgba(28, 66, 32, 0.9))',
              border: 'none',
              backdropFilter: 'blur(5px)',
              boxShadow: '0 4px 15px rgba(28, 66, 32, 0.3)',
              transition: 'all 0.3s ease'
            }}
          >
            Entrar
          </Button>

          <div className="d-flex align-items-center my-3">
            <hr className="flex-grow-1" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }} />
            <span className="mx-3 text-muted">ou</span>
            <hr className="flex-grow-1" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }} />
          </div>

          <Button
            variant="light"
            className="w-100 py-2 mb-3 rounded-pill d-flex align-items-center justify-content-center"
            onClick={() => handleLoginMicrosoft("popup")}
            style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(5px)',
              borderColor: 'rgba(143, 173, 21, 0.5)',
              color: '#1c4220',
              boxShadow: '0 4px 15px rgba(28, 66, 32, 0.2)',
              transition: 'all 0.3s ease'
            }}
          >
            <FaMicrosoft className="me-2" size={18} />
            <span className="fw-bold">Entrar com Microsoft</span>
          </Button>

          <div className="text-center mt-4">
            <Button
              variant="link"
              className="text-decoration-none d-flex align-items-center justify-content-center"
              onClick={goToSignUp}
              style={{ color: '#1c4220' }}
            >
              <FaUserPlus className="me-2" />
              <span>Não tem uma conta? <span className="fw-bold">Cadastre-se</span></span>
            </Button>
          </div>
        </Form>
      </Container>

      <div
        className="qrcode-download-card"
        style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          padding: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.65)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          boxShadow: '0 8px 32px 0 rgba(28, 66, 32, 0.37)',
          textAlign: 'center',
          zIndex: 10
        }}
      >
        <a
          href="https://mserverv2.motomco.com.br/sgu/app"
          target="_blank"
          rel="noopener noreferrer"
          className="qrcode-download-link"
        >
          <img
            className="qrcode-download-image"
            src="/images/qrcode-link-sgu.png"
            alt="QR Code para download do aplicativo SGU"
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
            }}
          />
          <p
            className="qrcode-download-label"
            style={{
              marginTop: '8px',
              marginBottom: 0,
              fontSize: '0.8rem',
              color: '#1c4220',
              fontWeight: 500
            }}
          >
            Baixe o app
          </p>
        </a>
      </div>

      {/* Forgot Password Modal */}
      <Modal
        show={forgotPasswordModal}
        onHide={() => {
          setForgotPasswordModal(false);
          setResetEmail('');
          setResetStatus({ message: '', type: '' });
        }}
        centered
        backdrop="static"
        contentClassName="border-0"
        dialogClassName="modal-glassmorphism"
      >
        <div
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 10px 40px 0 rgba(28, 66, 32, 0.25)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Close button positioned absolutely */}
          <Button
            variant="link"
            className="position-absolute"
            onClick={() => {
              setForgotPasswordModal(false);
              setResetEmail('');
              setResetStatus({ message: '', type: '' });
            }}
            style={{
              top: '15px',
              right: '15px',
              zIndex: 1050,
              color: '#1c4220',
              fontSize: '1.2rem',
              padding: '5px',
              lineHeight: 1,
              background: 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(5px)',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)'
            }}
          >
            &times;
          </Button>

          <Modal.Header
            style={{
              border: 'none',
              paddingBottom: 0,
              background: 'rgba(255, 255, 255, 0.2)'
            }}
            closeButton={false}
          >
            <div className="w-100 text-center">
              <div className="d-flex justify-content-center mb-2">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: '70px',
                    height: '70px',
                    background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.2), rgba(28, 66, 32, 0.4))',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 4px 20px rgba(28, 66, 32, 0.3)',
                    color: '#8fad15'
                  }}
                >
                  <FaKey size={28} />
                </div>
              </div>
              <Modal.Title className="fw-bold" style={{ color: '#1c4220' }}>Recuperação de Senha</Modal.Title>
            </div>
          </Modal.Header>

          <Modal.Body className="px-4 pt-2" style={{
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))'
          }}>
            <p className="text-center text-muted mb-4">
              Informe seu e-mail abaixo e enviaremos um link para redefinição de senha.
            </p>

            <Form.Group className="mb-4">
              <Form.Label className="fw-bold" style={{ color: '#1c4220' }}>E-mail</Form.Label>
              <Form.Control
                type="email"
                placeholder="Digite seu e-mail"
                value={resetEmail}
                onChange={handleResetEmailChange}
                disabled={isSubmitting}
                style={{
                  padding: '12px 20px',
                  background: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(5px)',
                  border: '1px solid rgba(143, 173, 21, 0.3)',
                  boxShadow: 'inset 0 2px 5px rgba(0, 0, 0, 0.05)',
                  borderRadius: '10px'
                }}
              />
            </Form.Group>

            {resetStatus.message && (
              <Alert
                variant={resetStatus.type}
                className="p-3 mb-3"
                style={{
                  fontSize: '0.9rem',
                  background: resetStatus.type === 'danger'
                    ? 'rgba(220, 53, 69, 0.65)'
                    : 'rgba(40, 167, 69, 0.65)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid ' + (resetStatus.type === 'danger'
                    ? 'rgba(255, 255, 255, 0.18)'
                    : 'rgba(255, 255, 255, 0.18)'),
                  color: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 20px 0 ' + (resetStatus.type === 'danger'
                    ? 'rgba(220, 53, 69, 0.25)'
                    : 'rgba(40, 167, 69, 0.25)'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                <div>
                  <div className="d-flex justify-content-center mb-2">
                    {resetStatus.type === 'success' ? (
                      <FaCheckCircle size={24} />
                    ) : (
                      <FaExclamationTriangle size={24} />
                    )}
                  </div>
                  <div>{resetStatus.message}</div>
                </div>
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-between border-0 pt-0" style={{
            background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05))'
          }}>
            <Button
              variant="link"
              onClick={() => {
                setForgotPasswordModal(false);
                setResetEmail('');
                setResetStatus({ message: '', type: '' });
              }}
              className="d-flex align-items-center"
              style={{
                color: '#1c4220',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                padding: '8px 16px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(5px)'
              }}
              disabled={isSubmitting}
            >
              <FaArrowLeft className="me-2" size={14} />
              Voltar
            </Button>

            <Button
              variant="primary"
              onClick={handleForgotPassword}
              disabled={isSubmitting}
              style={{
                background: 'linear-gradient(135deg, rgba(143, 173, 21, 0.9), rgba(28, 66, 32, 0.9))',
                border: 'none',
                backdropFilter: 'blur(5px)',
                boxShadow: '0 4px 15px rgba(28, 66, 32, 0.3)',
                transition: 'all 0.3s ease',
                borderRadius: '10px',
                padding: '8px 20px'
              }}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar Link'}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </Container>
  );
};

export default Login;
